import { Component } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule, I18nPluralPipe } from '@angular/common';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatError, MatFormField, MatLabel, MatPrefix, MatHint } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption } from '@angular/material/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { AvatarImageDirective } from './avatar-image.directive';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { WheelActionComponent } from '../../common/wheel-action/wheel-action.component';
import { LABELS } from '../../../core/constants/label-constants';
import { ConfigService } from './config.service';
import { HelperService, noWhitespaceValidation } from '../../../core/services/helper/helper.service';
import { MatTooltip } from '@angular/material/tooltip';
import { MatCardContent } from '@angular/material/card';
import { CommonService } from '../common-service/common.service';
import { ProductService } from '../product-management/product.service';

@Component({
    selector: 'app-configuration',
    standalone: true,
    imports: [
        FormsModule,
        I18nPluralPipe,
        MatButton,
        MatIconButton,
        MatCheckbox,
        MatFormField,
        MatIcon,
        MatInput,
        MatLabel,
        MatOption,
        MatPaginator,
        MatPrefix,
        MatHint,
        MatSelect,
        AvatarImageDirective,
        MatSlideToggle,
        WheelActionComponent,
        MatTooltip,
        MatCardContent,
        MatError,
        ReactiveFormsModule,
        CommonModule
    ],
    templateUrl: './configuration.component.html',
    styleUrl: './configuration.component.scss',
})
export class ConfigurationComponent {
    configForm: any = FormGroup;
    isLoad: boolean = false;
    routingPage = LABELS.CONFIGURATION;
    itemTypeList: any[] = [];
    itemsByType: Record<string, any[]> = {};
    loadingItemsByType: Record<string, boolean> = {};

    constructor(
        private _apiService: ConfigService,
        public _helper: HelperService,
        private _fb: FormBuilder,
        private _commonService: CommonService,
        private _productService: ProductService
    ) {
        this._helper.pageName.next(this.routingPage);
        this.initializeFilerForm();
        this.getCountryAndType();
    }

    /**
     * Summary: This method is use for get country list and type list
     */
    getCountryAndType() {
        this._commonService.getCountryAndTypeList(false).subscribe({
            next: (res: any) => {
                if (res?.meta?.status == 1) {
                    const allowedTypes = ['top', 'bottom', 'footwear', 'shoes'];
                    this.itemTypeList = (res?.data?.itemType || []).filter((item: any) =>
                        allowedTypes.includes(item.value?.toLowerCase())
                    );
                }
            }
        });
    }

    /**
     * Summary: This method is use for initialize form
     */
    initializeFilerForm() {
        this.configForm = this._fb.group({
            grimConversionRate: [
                '',
                [
                    Validators.required,
                    noWhitespaceValidation.noWhitespaceValidator,
                ],
            ],
            dailyFreeTryonCount: [1],
            // referrals: this._fb.array([]),
            closetItemUploads: this._fb.array([]),
            likes: [
                0
            ],
            comments: [
                0
            ],
            logins: [
                0
            ],
            purchases: [
                0
            ],
            secondHandPurchases: [0],
            secondHandSwaps: [0],
            freeTryOn: [0],
            wardrobeTryOn: [0],
            surpriseMe: [0],
            dailyImageGeneration: [0],
            videoTryOn: [0],
            standardSubscribers: [0],
            premiumSubscribers: [0],
            usedItemSellerFee: [
                0,
                [
                    Validators.required,
                    Validators.min(0),
                    Validators.max(100)
                ]
            ],
            avatarItemsMale: this._fb.array([
                this._fb.group({ itemTypeId: [2, Validators.required], items: [[]] }),
                this._fb.group({ itemTypeId: [3, Validators.required], items: [[]] }),
                this._fb.group({ itemTypeId: [3, Validators.required], items: [[]] }),
                this._fb.group({ itemTypeId: [4, Validators.required], items: [[]] })
            ]),
            avatarItemsFemale: this._fb.array([
                this._fb.group({ itemTypeId: [2, Validators.required], items: [[]] }),
                this._fb.group({ itemTypeId: [3, Validators.required], items: [[]] }),
                this._fb.group({ itemTypeId: [3, Validators.required], items: [[]] }),
                this._fb.group({ itemTypeId: [4, Validators.required], items: [[]] })
            ])
        });

        // Pre-fetch items for default types 2, 3, 3, 4; index 1 is one-piece.
        [2, 3, 3, 4].forEach((id, index) => {
            this.getItemsByType(id, 'male', index);
            this.getItemsByType(id, 'female', index);
        });
    }

    get referrals(): FormArray {
        return this.configForm.get('referrals') as FormArray;
    }

    // Create single referral object
    createReferral(noOfReferrals: number, earnings: any): FormGroup {
        return this._fb.group({
            noOfReferrals: [noOfReferrals],
            earnings: [earnings == '' ? 0 : earnings]
        });
    }

    // Default data
    setDefaultReferrals(val: any): void {
        let data = [
            { noOfReferrals: 1, earnings: 50 },
            { noOfReferrals: 3, earnings: 150 },
            { noOfReferrals: 5, earnings: 150 },
            { noOfReferrals: 10, earnings: 250 },
            { noOfReferrals: 20, earnings: 400 }
        ];
        if (val.length > 0) {
            data = val
        }

        data.forEach(item =>
            this.referrals.push(
                this.createReferral(item.noOfReferrals, item.earnings)
            )
        );
    }

    addReferral(): void {
        this.referrals.push(this.createReferral(0, 0));
    }

    removeReferral(index: number): void {
        this.referrals.removeAt(index);
    }


    get closetItems(): FormArray {
        return this.configForm.get('closetItemUploads') as FormArray;
    }

    // Create single referral object
    createClosetItems(itemUploaded: number, earnings: any): FormGroup {
        return this._fb.group({
            itemUploaded: [itemUploaded],
            earnings: [earnings == '' ? 0 : earnings]
        });
    }

    // Default data
    setDefaultClosetItems(val: any): void {
        let data = [
            { itemUploaded: 1, earnings: 20 },
            { itemUploaded: 3, earnings: 55 },
            { itemUploaded: 5, earnings: 75 },
            { itemUploaded: 8, earnings: 80 },
            { itemUploaded: 10, earnings: 80 }
        ];
        if (val.length > 0) {
            data = val
        }

        data.forEach(item =>
            this.closetItems.push(
                this.createClosetItems(item.itemUploaded, item.earnings)
            )
        );
    }

    addClosetItems(): void {
        this.closetItems.push(this.createClosetItems(0, 0));
    }

    removeClosetItems(index: number): void {
        this.closetItems.removeAt(index);
    }

    get avatarItemsMale(): FormArray {
        return this.configForm.get('avatarItemsMale') as FormArray;
    }

    get avatarItemsFemale(): FormArray {
        return this.configForm.get('avatarItemsFemale') as FormArray;
    }

    addMaleAvatarItem(value?: any) {
        const group = this._fb.group({
            itemTypeId: [value?.itemTypeId || null, Validators.required],
            items: [value?.items || []],
        });
        this.avatarItemsMale.push(group);
        if (value?.itemTypeId) {
            this.getItemsByType(value.itemTypeId, 'male', this.avatarItemsMale.length - 1);
        }
    }

    removeMaleAvatarItem(index: number) {
        this.avatarItemsMale.removeAt(index);
    }

    addFemaleAvatarItem(value?: any) {
        const group = this._fb.group({
            itemTypeId: [value?.itemTypeId || null, Validators.required],
            items: [value?.items || []],
        });
        this.avatarItemsFemale.push(group);
        if (value?.itemTypeId) {
            this.getItemsByType(value.itemTypeId, 'female', this.avatarItemsFemale.length - 1);
        }
    }

    removeFemaleAvatarItem(index: number) {
        this.avatarItemsFemale.removeAt(index);
    }

    onItemTypeChange(genderId: string, index: number, itemTypeId: number) {
        const array = genderId === 'male' ? this.avatarItemsMale : this.avatarItemsFemale;
        array.at(index).get('items')?.setValue([]);
        this.getItemsByType(itemTypeId, genderId, index);
    }

    getItemsByTypeKey(itemTypeId: number, genderId: string, index: number): string {
        return `${genderId}_${itemTypeId}_${index === 1}`;
    }

    getItemsByType(itemTypeId: number, genderId: string, index: number) {
        const key = this.getItemsByTypeKey(itemTypeId, genderId, index);
        if (!itemTypeId || this.itemsByType[key] || this.loadingItemsByType[key]) return;
        this.loadingItemsByType[key] = true;
        this._productService.listItemByType({ page: 1, status: 1, itemTypeId, genderId, isOnePiece: index === 1 }).subscribe({
            next: (res: any) => {
                const data = res?.data;
                this.itemsByType[key] = Array.isArray(data)
                    ? data
                    : data?.rows || data?.list || data?.data || [];
                this.loadingItemsByType[key] = false;
            },
            error: () => {
                this.itemsByType[key] = [];
                this.loadingItemsByType[key] = false;
            },
        });
    }

    isItemTypeSelected(genderId: string, itemTypeId: number, currentIndex: number): boolean {
        const array = genderId === 'male' ? this.avatarItemsMale : this.avatarItemsFemale;
        return array.controls.some((control, index) =>
            index !== currentIndex && Number(control.get('itemTypeId')?.value) === Number(itemTypeId)
        );
    }

    getItemTypeName(itemTypeId: number): string {
        const type = this.itemTypeList.find(t => Number(t.id) === Number(itemTypeId));
        if (type) return type.value;
        const fallbacks: Record<number, string> = {
            2: 'Top',
            3: 'Bottom',
            4: 'Footwear'
        };
        return fallbacks[itemTypeId] || `Type ${itemTypeId}`;
    }

    ngOnInit() {
        this.viewConfig();
    }

    viewConfig() {
        this._apiService.view().subscribe((res: any) => {
            if (res?.meta?.status == 1) {
                this.configForm.patchValue({
                    grimConversionRate: res?.data?.grimConversionRate
                        ? res.data.grimConversionRate
                        : '10',
                    dailyFreeTryonCount: res?.data?.dailyFreeTryonCount
                        ? res.data.dailyFreeTryonCount
                        : 1,
                    likes: res?.data?.likes || 0,
                    comments: res?.data?.comments || 0,
                    logins: res?.data?.logins || 0,
                    purchases: res?.data?.purchases || 0,
                    secondHandPurchases: res?.data?.secondHandPurchases || 0,
                    secondHandSwaps: res?.data?.secondHandSwaps || 0,
                    freeTryOn: res?.data?.freeTryOn || 0,
                    dailyImageGeneration: res?.data?.dailyImageGeneration || 0,
                    videoTryOn: res?.data?.videoTryOn || 0,
                    wardrobeTryOn: res?.data?.wardrobeTryOn || 0,
                    surpriseMe: res?.data?.surpriseMe || 0,
                    standardSubscribers: res?.data?.standardSubscribers || 0,
                    premiumSubscribers: res?.data?.premiumSubscribers || 0,
                    usedItemSellerFee: res?.data?.usedItemSellerFee || 0,
                });
                // this.setDefaultReferrals(res?.data?.referrals);
                this.setDefaultClosetItems(res?.data?.closetItemUploads);
                const maleItemsFromDb = res?.data?.avatarItemsMale || [];
                this.avatarItemsMale.clear();
                [2, 3, 3, 4].forEach((id, index) => {
                    const existing = maleItemsFromDb.find((item: any) => Number(item.itemTypeId) === id && Boolean(item.isOnePiece) === (index === 1));
                    this.addMaleAvatarItem({
                        itemTypeId: id,
                        items: existing ? existing.items : []
                    });
                });
                const femaleItemsFromDb = res?.data?.avatarItemsFemale || [];
                this.avatarItemsFemale.clear();
                [2, 3, 3, 4].forEach((id, index) => {
                    const existing = femaleItemsFromDb.find((item: any) => Number(item.itemTypeId) === id && Boolean(item.isOnePiece) === (index === 1));
                    this.addFemaleAvatarItem({
                        itemTypeId: id,
                        items: existing ? existing.items : []
                    });
                });
            } else {
                this._helper.toast(res?.meta?.message, 'error');
            }
        });
    }

    /**
     * Summary: This is use for add / edit config
     */
    addEdit() {
        if (this.configForm.valid) {

            if (!this.configForm?.controls['likes']?.value) {
                this.configForm?.controls['likes']?.setValue(0)
            }
            if (!this.configForm?.controls['comments']?.value) {
                this.configForm?.controls['comments']?.setValue(0)
            }
            if (!this.configForm?.controls['logins']?.value) {
                this.configForm?.controls['logins']?.setValue(0)
            }
            if (!this.configForm?.controls['purchases']?.value) {
                this.configForm?.controls['purchases']?.setValue(0)
            }
            if (!this.configForm?.controls['secondHandPurchases']?.value) {
                this.configForm?.controls['secondHandPurchases']?.setValue(0)
            }
            if (!this.configForm?.controls['secondHandSwaps']?.value) {
                this.configForm?.controls['secondHandSwaps']?.setValue(0)
            }
            if (!this.configForm?.controls['freeTryOn']?.value) {
                this.configForm?.controls['freeTryOn']?.setValue(0)
            }
            if (!this.configForm?.controls['dailyImageGeneration']?.value) {
                this.configForm?.controls['dailyImageGeneration']?.setValue(0)
            }
            if (!this.configForm?.controls['videoTryOn']?.value) {
                this.configForm?.controls['videoTryOn']?.setValue(0)
            }
            if (!this.configForm?.controls['wardrobeTryOn']?.value) {
                this.configForm?.controls['wardrobeTryOn']?.setValue(0)
            }
            if (!this.configForm?.controls['surpriseMe']?.value) {
                this.configForm?.controls['surpriseMe']?.setValue(0)
            }
            if (!this.configForm?.controls['standardSubscribers']?.value) {
                this.configForm?.controls['standardSubscribers']?.setValue(0)
            }
            if (!this.configForm?.controls['premiumSubscribers']?.value) {
                this.configForm?.controls['premiumSubscribers']?.setValue(0)
            }
            if (!this.configForm?.controls['usedItemSellerFee']?.value) {
                this.configForm?.controls['usedItemSellerFee']?.setValue(0)
            }
            this.isLoad = true;
            const payload = {
                ...this.configForm.value,
                avatarItemsMale: (this.configForm.value.avatarItemsMale || []).map((item: any, index: number) => ({
                    ...item,
                    isOnePiece: index === 1
                })),
                avatarItemsFemale: (this.configForm.value.avatarItemsFemale || []).map((item: any, index: number) => ({
                    ...item,
                    isOnePiece: index === 1
                }))
            };
            this._apiService.addEdit(payload).subscribe(
                (res: any) => {
                    if (res?.meta?.status === 1) {
                        this.isLoad = false;
                        this._helper.toast(res.meta.message, 'success');
                        this.closetItems.controls = [];
                        // this.referrals.controls = [];
                        this.setDefaultClosetItems(res?.data?.closetItemUploads)
                        // this.setDefaultReferrals(res?.data?.referrals)
                        const maleItemsFromDb = res?.data?.avatarItemsMale || [];
                        this.avatarItemsMale.clear();
                        [2, 3, 3, 4].forEach((id, index) => {
                            const existing = maleItemsFromDb.find((item: any) => Number(item.itemTypeId) === id && Boolean(item.isOnePiece) === (index === 1));
                            this.addMaleAvatarItem({
                                itemTypeId: id,
                                items: existing ? existing.items : []
                            });
                        });
                        const femaleItemsFromDb = res?.data?.avatarItemsFemale || [];
                        this.avatarItemsFemale.clear();
                        [2, 3, 3, 4].forEach((id, index) => {
                            const existing = femaleItemsFromDb.find((item: any) => Number(item.itemTypeId) === id && Boolean(item.isOnePiece) === (index === 1));
                            this.addFemaleAvatarItem({
                                itemTypeId: id,
                                items: existing ? existing.items : []
                            });
                        });
                    } else {
                        this.isLoad = false;
                        this._helper.toast(res.meta.message, 'error');
                    }
                },
                (error) => {
                    this.isLoad = false;
                }
            );
        } else {
            this.isLoad = false;
        }
    }

    /**
     * Summary: Prevents negative values and exponential notation in number inputs
     */
    preventNegativeInput(event: KeyboardEvent): void {
        if (event.key === '-' || event.key === 'e' || event.key === 'E') {
            event.preventDefault();
        }
    }

    /**
     * Summary: Ensures a number input value does not exceed 100 or drop below 0
     */
    validateMax100(event: any, controlName: string): void {
        const input = event.target as HTMLInputElement;
        let value = input.value;
        if (value !== '') {
            const num = parseFloat(value);
            if (num > 100) {
                input.value = '100';
                this.configForm.get(controlName)?.setValue(100);
            } else if (num < 0) {
                input.value = '0';
                this.configForm.get(controlName)?.setValue(0);
            }
        }
    }

    onRefreshTable() {
        this.viewConfig()
    }
    protected readonly LABELS = LABELS;
    protected readonly occasionForm = FormGroup;
}
